<template>
  <el-dialog
    v-if="visible"
    title="New Instant Poll"
    :visible="visible"
    top="5vh"
    @close="handleCloseDialog('close')"
  >
    <div>
      <el-form
        class="instant-poll"
        :model="form"
        :rules="rules"
        label-width="190px"
        label-position="left"
        ref="instantPollForm"
      >
        <el-form-item label="Title" prop="campaignTitle">
          <el-input v-model="form.campaignTitle" clearable />
        </el-form-item>

        <el-form-item label="Dates" prop="campaignDates">
          <el-date-picker
            style="width: 100%"
            v-model="form.campaignDates"
            type="datetimerange"
            :picker-options="pickerOptions"
            range-separator="To"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="Channels" prop="channels">
          <el-cascader
            class="full-width"
            v-model="form.channels"
            :options="groupedChannels"
            :props="{ multiple: true, checkStrictly: false }"
            clearable
          />
        </el-form-item>
        <el-form-item label="Only count unique" prop="isUnique">
          <el-checkbox v-model="form.isUnique" />
        </el-form-item>
        <el-form-item label="Keyword">
          <el-tag
            :key="tag"
            style="margin-left: 2px"
            v-for="tag in form.channelKeyword"
            :disable-transitions="false"
            closable
            @close="handleClose(tag)"
            >{{ tag }}</el-tag
          >

          <el-input
            class="input-new-tag"
            v-if="keywordInputVisible"
            v-model="keyword"
            ref="saveTagInput"
            @keyup.enter.native="handleInputConfirm"
            @blur="handleInputConfirm"
          ></el-input>
          <span>
            <el-button size="small" @click="showInput">+ Add keyword</el-button>
            <p class="small">Press enter key after each keyword added.</p>
          </span>
        </el-form-item>

        <div class="footer">
          <div class="pull-right">
            <el-button type="default" @click="handleCloseDialog('cancel')">Cancel</el-button>
            <el-button type="primary" @click="handleCreateInstantPoll" :disabled="mutationLoading">
              <i class="fa fa-spinner fa-spin" v-if="mutationLoading"></i>
              Save
            </el-button>
          </div>
        </div>
      </el-form>
    </div>
  </el-dialog>
</template>

<script>
import { groupBy } from 'lodash';
import moment from 'moment';
import createInstantPollCampaignMutation from '@/graphql/createInstantPollCampaignMutation.gql';

export default {
  name: 'InstantPoll',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    channels: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    pollChoices() {
      return this.form.channelKeyword.map(choice => ({ choice, matches: [choice] }));
    },
    channelsIds() {
      return this.form.channels.map(([, channelId]) => parseInt(channelId, 10));
    },
    groupedChannels() {
      const channelTitles = { Dms: 'DMS' };
      return Object.entries(groupBy(this.channels, 'channel_type_title'))
        .filter(([label]) => label !== 'Survey')
        .sort()
        .map(([label, children]) => ({
          label: channelTitles[label] || label,
          value: label,
          children: children
            .sort()
            .map(channel => ({ label: channel.handle, value: channel.id })),
        }));
    },
  },
  methods: {
    handleCloseDialog(event) {
      this.form = {
        campaignTitle: '',
        campaignDates: [],
        campaignType: 'INSTANT_POLL',
        perpetual: false,
        channelKeyword: [],
        channels: [],
        isUnique: true,
      };
      this.$emit(event);
    },
    handleCreateInstantPoll() {
      this.mutationLoading = true;
      this.$apollo
        .mutate({
          mutation: createInstantPollCampaignMutation,
          variables: {
            campaignTitle: this.form.campaignTitle,
            campaignStartDate: this.form.campaignDates[0],
            campaignEndDate: this.form.campaignDates[1],
            campaignType: this.form.campaignType,
            channelKeywords: this.form.channelKeyword,
            channelIds: this.channelsIds,
            pollQuestion: this.form.campaignTitle,
            pollChoices: this.pollChoices,
            winningDisqualificationDuration: null,
            isUnique: this.form.isUnique,
          },
        })
        .then(response => {
          const campaignId = parseInt(response.data.createCampaign.campaign.id, 10);
          this.$emit('createPanel', [this.form.campaignTitle, [campaignId], this.channelsIds]);
          this.mutationLoading = false;
          this.$notify({
            title: 'Success!',
            message: 'Your instant poll was successfully created',
            type: 'success',
          });
        })
        .catch(() => {
          this.mutationLoading = false;
          this.$notify({
            title: 'Error!',
            message: 'Unable to create your instant poll',
            type: 'error',
          });
        });
    },
    handlePickerShortcut(picker, amount, unit) {
      const startDate = moment().toDate();
      const endDate = moment().add(amount, unit).toDate();
      picker.$emit('pick', [startDate, endDate]);
    },
    handleInputConfirm() {
      const keyword = this.keyword;
      if (keyword) {
        this.form.channelKeyword.push(keyword);
      }
      this.keywordInputVisible = false;
      this.keyword = '';
    },
    showInput() {
      this.keywordInputVisible = true;
      this.$nextTick(tick => {
        this.nextTick = tick;
        this.$refs.saveTagInput.$refs.input.focus();
      });
    },
    handleClose(tag) {
      this.form.channelKeyword.splice(this.form.channelKeyword.indexOf(tag), 1);
    },
  },
  data: () => ({
    form: {
      campaignTitle: '',
      campaignDates: [],
      campaignType: 'INSTANT_POLL',
      perpetual: false,
      channelKeyword: [],
      channels: [],
    },
    mutationLoading: false,
    nextTick: '',
    keywordInputVisible: false,
    keyword: '',
    rules: {
      campaignTitle: [{ required: true, message: 'Please input the name of your instant poll' }],
      campaignDates: [
        { required: true, message: 'Please select the date range for your instant poll' },
      ],
    },
    pickerOptions: {
      shortcuts: [
        {
          text: '15 mins',
          onClick(picker) {
            const startDate = moment()
              .add(5, 'minute')
              .set({ seconds: 0, milliseconds: 0 })
              .toDate();
            const endDate = moment(startDate).add(15, 'minute').toDate();
            picker.$emit('pick', [startDate, endDate]);
          },
        },
        {
          text: '30 mins',
          onClick(picker) {
            const startDate = moment()
              .add(5, 'minute')
              .set({ seconds: 0, milliseconds: 0 })
              .toDate();
            const endDate = moment(startDate).add(30, 'minute').toDate();
            picker.$emit('pick', [startDate, endDate]);
          },
        },
        {
          text: '45 mins',
          onClick(picker) {
            const startDate = moment()
              .add(5, 'minute')
              .set({ seconds: 0, milliseconds: 0 })
              .toDate();
            const endDate = moment(startDate).add(45, 'minute').toDate();
            picker.$emit('pick', [startDate, endDate]);
          },
        },
        {
          text: '1 Hour',
          onClick(picker) {
            const startDate = moment()
              .add(5, 'minute')
              .set({ seconds: 0, milliseconds: 0 })
              .toDate();
            const endDate = moment(startDate).add(1, 'hour').toDate();
            picker.$emit('pick', [startDate, endDate]);
          },
        },
        {
          text: '1 Hour 30 Mins',
          onClick(picker) {
            const startDate = moment()
              .add(5, 'minute')
              .set({ seconds: 0, milliseconds: 0 })
              .toDate();
            const endDate = moment(startDate).add(1.5, 'hour').toDate();
            picker.$emit('pick', [startDate, endDate]);
          },
        },
        {
          text: '2 Hours',
          onClick(picker) {
            const startDate = moment()
              .add(5, 'minute')
              .set({ seconds: 0, milliseconds: 0 })
              .toDate();
            const endDate = moment(startDate).add(2, 'hour').toDate();
            picker.$emit('pick', [startDate, endDate]);
          },
        },
        {
          text: '3 Hours',
          onClick(picker) {
            const startDate = moment()
              .add(5, 'minute')
              .set({ seconds: 0, milliseconds: 0 })
              .toDate();
            const endDate = moment(startDate).add(3, 'hour').toDate();
            picker.$emit('pick', [startDate, endDate]);
          },
        },
      ],
    },
  }),
};
</script>

<style scoped>
.keyword-input {
  max-width: 350px;
}
</style>


// WEBPACK FOOTER //
// InstantPoll.vue?a76fa5b2